<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  company: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'remove']);
const { t } = useI18n();
const root = ref(null);
const open = ref(false);
const editLabel = computed(() =>
  props.company
    ? t('SALES_PARTIES.ACTIONS.EDIT_COMPANY')
    : t('SALES_PARTIES.ACTIONS.EDIT')
);
const removeLabel = computed(() =>
  props.company
    ? t('SALES_PARTIES.ACTIONS.REMOVE')
    : t('SALES_PARTIES.ACTIONS.REMOVE_PARTY')
);

const menuSections = computed(() => [
  {
    title: t('SALES_PARTIES.ACTIONS.OPTIONS_TITLE'),
    items: [
      {
        action: 'edit',
        value: 'edit',
        label: editLabel.value,
        icon: 'i-lucide-pencil',
      },
      {
        action: 'delete',
        value: 'remove',
        label: removeLabel.value,
        icon: 'i-lucide-x',
      },
    ],
  },
]);

const close = () => {
  open.value = false;
};
const select = item => {
  close();
  if (item.value === 'edit') emit('edit');
  if (item.value === 'remove') emit('remove');
};
const closeOnOutside = event => {
  if (!root.value?.contains(event.target)) close();
};

onMounted(() => document.addEventListener('mousedown', closeOnOutside));
onBeforeUnmount(() =>
  document.removeEventListener('mousedown', closeOnOutside)
);
</script>

<template>
  <div ref="root" class="relative" @keydown.esc.stop="close">
    <button
      type="button"
      class="grid size-8 place-items-center rounded-full text-lais-text-muted transition hover:bg-lais-primary-50 hover:text-lais-primary focus-visible:outline-none focus-visible:shadow-lais-focus"
      :aria-label="t('SALES_PARTIES.ACTIONS.OPTIONS_TITLE')"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      data-test="sales-party-actions-trigger"
      @click.stop="open = !open"
    >
      <span class="i-lucide-ellipsis size-4" aria-hidden="true" />
    </button>
    <DropdownMenu
      v-if="open"
      :menu-sections="menuSections"
      class="right-0 top-full mt-1 w-48"
      data-test="sales-party-actions-menu"
      @click.stop
      @action="select"
    />
  </div>
</template>
